import React, { type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import {
  TIMELINE_AXIS_LABEL_CLASS,
  TIMELINE_INK,
  type TimelineInkVariant,
} from '@/components/config_forms/shared/help-graphics/timeline/timelineTextTokens';

/* ── Types ──────────────────────────────────────────── */

export interface TimelineZone {
  id: string;
  flex: number;
  variant: 'safe' | 'v1' | 'v1-alt' | 'v2' | 'v2-alt'
         | 'overlap' | 'overlap-alt' | 'break' | 'ellipsis';
  content?: ReactNode;
  borderRightOverride?: string;
  isDeadlineBoundary?: boolean;
  bgOverride?: string;
  className?: string;
}

export interface ViolationSpan {
  id: string;
  label: string;
  variant: 'v1' | 'v2';
  leftPct: number;
  rightPct: number;
}

export interface TimeAxisTick {
  id: string;
  label: string;
  leftPct: number;
  align: 'left' | 'center' | 'right';
  variant: TimelineInkVariant;
  /** 0–1 opacity for animated show/hide. Defaults to 1. */
  opacity?: number;
}

/* ── Style Constants ────────────────────────────────── */

export const HATCH_BG = (color: string) =>
  `repeating-linear-gradient(-55deg, ${color}, ${color} 3px, transparent 3px, transparent 7px)`;

const ZONE_BG_STYLES: Record<TimelineZone['variant'], { bg: string; borderRight?: string }> = {
  safe:           { bg: 'bg-scenario-green/[0.08]', borderRight: 'border-r-2 border-r-scenario-green/30' },
  v1:             { bg: 'bg-primary/[0.06]',        borderRight: 'border-r border-r-primary/25 border-dashed' },
  'v1-alt':       { bg: 'bg-primary/[0.10]',        borderRight: 'border-r border-r-primary/25 border-dashed' },
  v2:             { bg: 'bg-destructive/[0.08]',     borderRight: 'border-r border-r-destructive/25 border-dashed' },
  'v2-alt':       { bg: 'bg-destructive/[0.12]',     borderRight: 'border-r border-r-destructive/25 border-dashed' },
  overlap:        { bg: 'bg-destructive/[0.08]',     borderRight: 'border-r border-r-destructive/25 border-dashed' },
  'overlap-alt':  { bg: 'bg-destructive/[0.12]',     borderRight: 'border-r border-r-destructive/25 border-dashed' },
  break:          { bg: 'bg-scenario-green/[0.04]' },
  ellipsis:       { bg: '' },
};

export const SPAN_STYLES: Record<'v1' | 'v2', { bg: string; border: string; labelColor: string }> = {
  v1: {
    bg:         'color-mix(in srgb, var(--primary), transparent 94%)',
    border:     'color-mix(in srgb, var(--primary), transparent 85%)',
    labelColor: 'var(--primary-text)',
  },
  v2: {
    bg:         'color-mix(in srgb, var(--color-destructive), transparent 94%)',
    border:     'color-mix(in srgb, var(--color-destructive), transparent 85%)',
    labelColor: 'var(--destructive-text)',
  },
};

const TIMELINE_MOBILE_TEXT = 'text-[10px] min-[375px]:text-[11px] sm:text-[12px]';

/* ── Layout Math ────────────────────────────────────── */

export function computeZoneLayout(
  zones: Array<{ flex: number }>,
  opts?: { minPct?: number },
): { widths: number[]; lefts: number[] } {
  const totalFlex = zones.reduce((sum, z) => sum + z.flex, 0);
  if (totalFlex === 0) return { widths: zones.map(() => 0), lefts: zones.map(() => 0) };

  const rawPcts = zones.map(z => (z.flex / totalFlex) * 100);
  const widths = [...rawPcts];

  const minPct = opts?.minPct;
  if (minPct != null) {
    let deficit = 0;
    for (let i = 0; i < widths.length; i++) {
      if (zones[i].flex === 0) continue;
      if (widths[i] < minPct) {
        deficit += minPct - widths[i];
        widths[i] = minPct;
      }
    }
    if (deficit > 0) {
      const aboveMinTotal = rawPcts.reduce(
        (s, p, i) => s + (zones[i].flex > 0 && p >= minPct ? p : 0),
        0,
      );
      for (let i = 0; i < widths.length; i++) {
        if (zones[i].flex > 0 && rawPcts[i] >= minPct) {
          widths[i] -= deficit * (rawPcts[i] / aboveMinTotal);
        }
      }
    }
  }

  const lefts: number[] = [];
  let cum = 0;
  for (const w of widths) {
    lefts.push(cum);
    cum += w;
  }
  return { widths, lefts };
}

/* ── Rendering Components ───────────────────────────── */

const TRANSITION_CLASSES = 'motion-safe:transition-[left,width,opacity] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)]';

export function TimelineBar({
  zones,
  className,
  layoutOpts,
}: {
  zones: TimelineZone[];
  className?: string;
  layoutOpts?: { minPct?: number };
}) {
  const { widths, lefts } = computeZoneLayout(zones, layoutOpts);
  const defaultHatchColor = 'color-mix(in srgb, var(--primary), transparent 94%)';

  return (
    <div
      className={cn('relative h-14 rounded-[0.25rem] overflow-hidden', className)}
      style={{
        boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--border), transparent 50%)',
      }}
    >
      {zones.map((zone, i) => {
        const style = ZONE_BG_STYLES[zone.variant];
        const isEllipsis = zone.variant === 'ellipsis';
        // Thick (2px) borders render as positioned dividers below for exact alignment.
        // Only apply CSS border-right for thin (1px) variant borders.
        const hasThickBorder = zone.isDeadlineBoundary || style.borderRight?.includes('border-r-2');
        const showCssBorder = !hasThickBorder && !zone.borderRightOverride;

        return (
          <div
            key={zone.id}
            className={cn(
              'absolute top-0 bottom-0 flex flex-col items-center justify-center min-w-0 overflow-hidden',
              TRANSITION_CLASSES,
              !zone.bgOverride && !isEllipsis && style.bg,
              showCssBorder && style.borderRight,
              zone.borderRightOverride,
              zone.className,
            )}
            data-anim-audit="timeline-zone"
            data-anim-audit-zone-id={zone.id}
            style={{
              width: `${widths[i]}%`,
              left: `${lefts[i]}%`,
              opacity: zone.flex === 0 ? 0 : 1,
              ...(zone.bgOverride
                ? { background: zone.bgOverride }
                : isEllipsis
                  ? { background: HATCH_BG(defaultHatchColor) }
                  : {}),
            }}
          >
            {zone.content}
          </div>
        );
      })}

      {/* Thick (2px) dividers — rendered as separate elements at exact boundary
          positions so they align precisely with ticks and span edges.
          CSS border-right is inside box-sizing:border-box, causing a 1px offset. */}
      {zones.flatMap((zone, i) => {
        if (zone.flex === 0) return [];
        const dividers: React.ReactNode[] = [];
        const style = ZONE_BG_STYLES[zone.variant];
        const hasThickVariantBorder = style.borderRight?.includes('border-r-2');
        const solidColor = 'color-mix(in srgb, var(--color-scenario-green), transparent 70%)';
        const dashedBg = `repeating-linear-gradient(to bottom, ${solidColor} 0px, ${solidColor} 3px, transparent 3px, transparent 7px)`;

        // Right-edge divider for deadline boundaries and thick variant borders (e.g. safe zone).
        // Always render so CSS transitions can animate position; fade via opacity near the edge.
        if (zone.isDeadlineBoundary || hasThickVariantBorder) {
          const pct = lefts[i] + widths[i];
          dividers.push(
            <div
              key={`div-r-${zone.id}`}
              className={cn('absolute top-0 bottom-0 pointer-events-none', TRANSITION_CLASSES)}
              style={{ left: `${pct}%`, width: '2px', transform: 'translateX(-1px)', background: solidColor, opacity: pct >= 99.5 ? 0 : 1 }}
            />,
          );
        }

        // Both-edge dividers for break zones (dashed)
        if (zone.variant === 'break') {
          const leftPct = lefts[i];
          const rightPct = lefts[i] + widths[i];
          if (leftPct > 0.1) {
            dividers.push(
              <div
                key={`div-l-${zone.id}`}
                className={cn('absolute top-0 bottom-0 pointer-events-none', TRANSITION_CLASSES)}
                style={{ left: `${leftPct}%`, width: '2px', transform: 'translateX(-1px)', background: dashedBg }}
              />,
            );
          }
          if (rightPct < 99.9) {
            dividers.push(
              <div
                key={`div-r-${zone.id}`}
                className={cn('absolute top-0 bottom-0 pointer-events-none', TRANSITION_CLASSES)}
                style={{ left: `${rightPct}%`, width: '2px', transform: 'translateX(-1px)', background: dashedBg }}
              />,
            );
          }
        }

        return dividers;
      })}
    </div>
  );
}

function SpanBar({ span }: { span: ViolationSpan }) {
  const s = SPAN_STYLES[span.variant];
  return (
    <div
      className={cn(
        'absolute top-0 h-full flex items-center justify-center rounded-t-[0.25rem]',
        'motion-safe:transition-[left,right,opacity] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)]',
      )}
      data-anim-audit="timeline-span"
      data-anim-audit-span-id={span.id}
      style={{
        left: `${span.leftPct}%`,
        right: `${span.rightPct}%`,
        opacity: span.leftPct >= 100 - span.rightPct ? 0 : 1,
        background: s.bg,
        borderTop: `1px solid ${s.border}`,
        borderLeft: `1px solid ${s.border}`,
        borderRight: `1px solid ${s.border}`,
      }}
    >
      <span
        className={cn(
          'font-heading uppercase font-semibold whitespace-nowrap leading-none',
          TIMELINE_MOBILE_TEXT,
          'tracking-[0.75px] sm:tracking-[1px]',
        )}
        style={{ color: s.labelColor }}
      >
        {span.label}
      </span>
    </div>
  );
}

export function ViolationSpans({ spans, stacked = true }: { spans: ViolationSpan[]; stacked?: boolean }) {
  if (spans.length === 0) return null;
  const SPAN_ROW_HEIGHT = 14;

  // Stacked layout — each span gets its own row.
  // Rows collapse to 0fr when their span is off-screen (leftPct >= 100),
  // and expand to 1fr when visible, animated via grid-template-rows CSS
  // transition. This avoids the 14px height snap when toggling between
  // single-row and multi-row layouts (e.g. recurring deadline toggle).
  if (stacked && spans.length > 1) {
    return (
      <div className="flex flex-col w-full" style={{ gap: 0, marginBottom: 0 }}>
        {spans.map((span) => {
          const isVisible = span.leftPct < 100 - span.rightPct;
          return (
            <div
              key={span.id}
              className="grid w-full motion-safe:transition-[grid-template-rows] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)]"
              style={{ gridTemplateRows: isVisible ? `${SPAN_ROW_HEIGHT}px` : '0px' }}
            >
              <div className="relative h-[14px] w-full overflow-hidden min-h-0">
                <SpanBar span={span} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="relative h-[14px] w-full" style={{ marginBottom: 0 }}>
      {spans.map((span) => (
        <SpanBar key={span.id} span={span} />
      ))}
    </div>
  );
}

export function TimeAxis({
  ticks,
  endContent,
}: {
  ticks: TimeAxisTick[];
  endContent?: ReactNode;
}) {
  return (
    <div>
      <div className={cn('relative h-[32px] pt-1 font-mono leading-[1.2]', TIMELINE_MOBILE_TEXT)}>
        {ticks.map((tick) => {
          if (!tick.label) return null;
          const transform =
            tick.align === 'left'
              ? 'none'
              : tick.align === 'right'
                ? 'translateX(-100%)'
                : 'translateX(-50%)';
          const color = TIMELINE_INK[tick.variant];
          return (
            <span
              key={tick.id}
              className={cn(
                'absolute whitespace-pre',
                tick.align === 'right' ? 'text-right' : tick.align === 'left' ? 'text-left' : 'text-center',
                'motion-safe:transition-[left,opacity] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)]',
                tick.variant !== 'label' && 'font-normal leading-[1.15] tabular-nums',
                tick.variant === 'label'
                  && TIMELINE_AXIS_LABEL_CLASS,
              )}
              data-anim-audit="timeline-tick"
              data-anim-audit-tick-id={tick.id}
              style={{
                left: `${tick.leftPct}%`,
                transform,
                ...(color ? { color } : {}),
                ...(tick.opacity != null ? { opacity: tick.opacity } : {}),
              }}
            >
              {tick.label}
            </span>
          );
        })}
      </div>
      {endContent && <div className="mt-1">{endContent}</div>}
    </div>
  );
}
